<template>
  <div class="open-fastpay-page">
    <div class="open-fastpay-shell">
      <div v-if="loading" class="state-box">加载中...</div>
      <div v-else-if="loadError" class="state-box error">{{ loadError }}</div>

      <section v-else-if="resultView === 'success'" class="result show">
        <div class="result-inner">
          <img class="result-image" :src="successIconUrl" alt="绑卡成功" />
          <div class="result-title">绑卡成功</div>
          <div class="result-subtitle">协议扣款已开通，商户可直接发起扣款</div>
          <div class="result-card">
            <div v-if="merchantNameText" class="result-row">
              <span>商户名称</span>
              <span>{{ merchantNameText }}</span>
            </div>
            <div class="result-row">
              <span>付款银行</span>
              <span>{{ bankDisplayName }}</span>
            </div>
            <div class="result-row">
              <span>银行卡号</span>
              <span>{{ successPanMask }}</span>
            </div>
            <div v-if="successCardHolderName" class="result-row">
              <span>持卡人</span>
              <span>{{ successCardHolderName }}</span>
            </div>
            <div class="result-row">
              <span>业务状态</span>
              <span class="success-text">成功</span>
            </div>
          </div>
          <div v-if="pendingRedirectUrl" class="result-tip">
            {{ redirectCountdown }} 秒后自动返回商户
          </div>
          <button
            v-if="pendingRedirectUrl"
            class="primary-btn finish-btn"
            @click="goToMerchant"
          >立即返回商户</button>
          <button
            v-else
            type="button"
            class="primary-btn finish-btn"
            @click="finishWithoutRedirect"
          >完成</button>
          <div class="customer" @click="openCustomerService">🎧 商户客服</div>
        </div>
      </section>

      <section v-else-if="resultView === 'fail'" class="result result-fail show">
        <div class="result-inner">
          <div class="result-fail-icon">!</div>
          <div class="result-title">绑卡失败</div>
          <div class="result-subtitle">请核对信息后重新尝试绑卡</div>
          <div class="result-error-box">
            {{ resultErrorMsg || '绑卡失败，请稍后重试' }}
          </div>
          <button type="button" class="primary-btn finish-btn" @click="retryBind">重新绑卡</button>
          <div class="customer" @click="openCustomerService">🎧 商户客服</div>
        </div>
      </section>

      <main v-else class="page">
        <section class="hero">
          <div class="hero-inner">
            <div class="hero-copy">
              <h1>{{ heroTitle }}</h1>
              <p>{{ heroSubtitle }}</p>
              <div v-if="showAmount" class="hero-amount">¥{{ amountText }}</div>
              <div class="hero-tags">
                <span>信息加密</span>
                <span>快速验证</span>
                <span>安全可靠</span>
              </div>
            </div>
          </div>
        </section>

        <section class="content">
          <div class="form-card">
            <div v-if="merchantNameText" class="form-row">
              <div class="label">商户名称</div>
              <div class="field">{{ merchantNameText }}</div>
            </div>

            <div
              class="form-row"
              :class="{ clickable: editableCardForm }"
              @click="editableCardForm && openBankSheet()"
            >
              <div class="label">付款银行</div>
              <div class="field" :class="{ clickable: editableCardForm }">
                <span>{{ bankDisplayName }}</span>
                <span v-if="editableCardForm" class="arrow">›</span>
              </div>
            </div>

            <div class="form-row">
              <div class="label">卡类型</div>
              <div class="field">{{ cardTypeText }}</div>
            </div>

            <div v-if="editableCardForm" class="form-row">
              <div class="label">卡号</div>
              <div class="field">
                <input
                  v-model="postData.pan"
                  type="tel"
                  inputmode="numeric"
                  autocomplete="cc-number"
                  maxlength="23"
                  placeholder="请输入银行卡号"
                  @input="onPanInput"
                  @focus="onInputFocus"
                />
              </div>
            </div>
            <div v-else class="form-row">
              <div class="label">卡号</div>
              <div class="field">{{ pageInfo.panMask || '-' }}</div>
            </div>

            <div v-if="editableCardForm" class="form-row">
              <div class="label">姓名</div>
              <div class="field">
                <input
                  v-model="postData.cardHolderName"
                  type="text"
                  autocomplete="name"
                  placeholder="持卡人姓名"
                  @focus="onInputFocus"
                />
              </div>
            </div>
            <div v-else class="form-row">
              <div class="label">姓名</div>
              <div class="field">{{ pageInfo.cardHolderName || '-' }}</div>
            </div>

            <div v-if="editableCardForm" class="form-row">
              <div class="label">证件号</div>
              <div class="field">
                <input
                  v-model="postData.cardHolderId"
                  type="text"
                  inputmode="text"
                  autocomplete="off"
                  placeholder="持卡人证件号码"
                  @focus="onInputFocus"
                />
              </div>
            </div>
            <div v-else-if="readOnlyMode" class="form-row">
              <div class="label">证件号</div>
              <div class="field">{{ pageInfo.cardHolderIdMask || '-' }}</div>
            </div>

            <div v-if="editableCardForm" class="form-row">
              <div class="label">手机号</div>
              <div class="field">
                <input
                  v-model="postData.phoneNo"
                  type="tel"
                  inputmode="tel"
                  autocomplete="tel"
                  maxlength="11"
                  placeholder="银行预留手机号"
                  @input="onPhoneInput"
                  @focus="onInputFocus"
                />
              </div>
            </div>
            <div v-else class="form-row">
              <div class="label">手机号</div>
              <div class="field">{{ pageInfo.phoneNoMask || '-' }}</div>
            </div>

            <div v-if="editableCardForm" class="form-row">
              <div class="label">短信验证码</div>
              <div class="field code-field">
                <input
                  v-model="validCode"
                  class="code-input"
                  type="tel"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="请输入验证码"
                  autocomplete="one-time-code"
                  @input="onSmsInput"
                  @focus="onInputFocus"
                />
                <button
                  class="code-btn"
                  :disabled="smsBtnDisabled"
                  @click="sendBindSms"
                >{{ smsBtnText }}</button>
              </div>
            </div>
            <div v-else-if="readOnlyMode && smsRequired" class="form-row">
              <div class="label">短信验证码</div>
              <div class="field">
                <input
                  v-model="validCode"
                  type="tel"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="请输入验证码"
                  autocomplete="one-time-code"
                  @input="onSmsInput"
                  @focus="onInputFocus"
                />
              </div>
            </div>
          </div>

          <div v-if="!alreadySigned" :key="'agreement-' + currentBankId" class="agreement" @click="toggleAgreeQuick">
            <div class="check" :class="{ active: protocolAgreed }">✓</div>
            <div class="agreement-text">
              <span>{{ protocolAgreed ? '已阅读并同意' : '请阅读并同意' }}</span>
              <template v-for="(item, idx) in effectiveProtocols">
                <a
                  :key="item.id"
                  href="javascript:;"
                  @click.stop="openProtocol(idx)"
                >《{{ item.title }}》</a><span
                  v-if="idx < effectiveProtocols.length - 1"
                  :key="'sep-' + item.id"
                >、</span>
              </template>
            </div>
          </div>

          <div v-if="alreadySigned" class="notice signed-notice">
            <div class="notice-icon info-dot">i</div>
            <div>该银行卡已签约，商户可直接发起支付，无需输入验证码。</div>
          </div>

          <div v-else class="notice">
            <div class="notice-icon info-dot">i</div>
            <div>为了保障正常银行卡签约，需要收集您的卡信息，全程保障隐私安全，请放心使用。</div>
          </div>

          <button class="primary-btn" :disabled="submitting" @click="handleSubmit">
            {{ submitBtnText }}
          </button>
          <div class="customer" @click="openCustomerService">🎧 商户客服</div>
        </section>
      </main>
    </div>

    <div class="mask" :class="{ show: showBankSheet }" @click="closeBankSheet"></div>
    <div class="sheet" :class="{ show: showBankSheet }">
      <div class="sheet-title">
        选择付款银行
        <div class="sheet-close" @click="closeBankSheet">×</div>
      </div>
      <input
        v-model="bankKeyword"
        class="bank-search"
        type="search"
        inputmode="search"
        enterkeyhint="search"
        placeholder="搜索银行"
        @focus="onInputFocus"
      />
      <div class="bank-list">
        <div
          v-for="item in filteredBankList"
          :key="item.bank_id"
          class="bank-item"
          :class="{ active: postData.bankID === item.bank_id }"
          @click="selectBank(item)"
        >
          <div class="bank-logo">{{ getBankShort(item.bank_name) }}</div>
          <div>
            <div class="bank-name">{{ item.bank_name }}</div>
            <div class="bank-desc">快捷支付业务服务协议</div>
          </div>
        </div>
      </div>
    </div>

    <div class="protocol" :class="{ show: showProtocol }">
      <div class="protocol-head">
        <div class="protocol-back" @click="closeProtocol">‹</div>
        <div class="protocol-head-title">{{ currentProtocolTitle }}</div>
      </div>
      <div v-if="effectiveProtocols.length > 1" class="protocol-tabs">
        <button
          v-for="(item, idx) in effectiveProtocols"
          :key="item.id"
          type="button"
          class="protocol-tab"
          :class="{ active: activeProtocolIndex === idx }"
          @click="switchProtocolTab(idx)"
        >{{ item.title }}</button>
      </div>
      <div ref="protocolBody" class="protocol-body" @scroll="onProtocolScroll">
        <div class="protocol-tip">请完整阅读全部协议，滑动至底部后才可点击同意。</div>
        <div v-if="protocolLoading" class="protocol-state">协议加载中...</div>
        <div v-else-if="protocolLoadError" class="protocol-state error">{{ protocolLoadError }}</div>
        <div v-else class="protocol-html" v-html="protocolHtml"></div>
        <p class="protocol-end">— 协议底部 —</p>
      </div>
      <div class="protocol-footer">
        <button class="primary-btn" :disabled="!protocolReachBottom" @click="confirmProtocol">
          {{ protocolReachBottom ? '我已阅读并同意' : '请阅读至底部' }}
        </button>
      </div>
    </div>

    <div class="toast" :class="{ show: toastVisible }">{{ toastText }}</div>

    <div class="customer-mask" :class="{ show: customerVisible }" @click.self="closeCustomerService">
      <div class="customer-panel">
        <div class="customer-title">商户客服电话</div>
        <a class="customer-phone" :href="'tel:' + merchantContactTel">{{ merchantContactTel }}</a>
        <button type="button" class="customer-close-btn" @click="closeCustomerService">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getOpenUpFastPageInfo, openUpFastBindSendSms, openUpFastBindConfirm } from '@/api/api'
import bankIdList from './bank-id.json'
import { getProtocolsByBankId, getDefaultProtocols, loadAgreementManifestFromServer } from './open-fastpay-protocol-config'
import successIconUrl from './bank_bind/assets/success_icon.png'
import './bank_bind/styles.css'
import './bank_bind/open-fastpay-shell.css'

const SMS_BTN_TEXT = '获取验证码'
const SMS_COOLDOWN = 60
const REDIRECT_COUNTDOWN = 3
const DESKTOP_BREAKPOINT = 481
const DESKTOP_WIDE_BREAKPOINT = 960
const DESKTOP_REM_BASE = 37.5
const DESKTOP_SHELL_WIDTH = 480
const DESKTOP_SHELL_WIDE_MAX = 920

export default {
  name: 'OpenFastpay',
  data() {
    return {
      loading: true,
      loadError: '',
      orderToken: '',
      ifCode: '',
      pageInfo: {},
      postData: {
        bankID: '102',
        pan: '',
        cardHolderName: '',
        cardHolderId: '',
        phoneNo: '',
        cardType: 1,
        token: ''
      },
      validCode: '',
      smsSent: false,
      smsBtnDisabled: false,
      smsBtnText: SMS_BTN_TEXT,
      smsCooldown: SMS_COOLDOWN,
      smsTimer: null,
      protocolAgreed: false,
      submitting: false,
      bankIdList,
      showBankSheet: false,
      bankKeyword: '',
      showProtocol: false,
      activeProtocolIndex: 0,
      protocolHtml: '',
      protocolLoading: false,
      protocolLoadError: '',
      protocolCache: {},
      protocolFallbackActive: false,
      protocolReachBottom: false,
      toastText: '',
      toastVisible: false,
      toastTimer: null,
      resultView: '',
      resultErrorMsg: '',
      successPanMask: '',
      successCardHolderName: '',
      pendingRedirectUrl: '',
      redirectCountdown: REDIRECT_COUNTDOWN,
      redirectCountdownTimer: null,
      customerVisible: false,
      successIconUrl,
      defaultDocumentTitle: ''
    }
  },
  computed: {
    heroTitle() {
      return this.alreadySigned ? '绑卡已完成' : '安全绑卡'
    },
    heroSubtitle() {
      return this.alreadySigned ? '协议扣款已开通，可直接返回商户' : '快捷验证，轻松完成绑定'
    },
    showAmount() {
      const amount = this.pageInfo.amount
      return amount != null && amount > 0
    },
    amountText() {
      const amount = this.pageInfo.amount
      if (amount === undefined || amount === null) {
        return '0.00'
      }
      return (amount / 100).toFixed(2)
    },
    alreadySigned() {
      if (this.readOnlyMode) {
        return false
      }
      return this.pageInfo.alreadySigned === true || this.pageInfo.bindStatus === 'success'
    },
    readOnlyMode() {
      return this.pageInfo.readOnlyMode === true
    },
    editableCardForm() {
      return !this.alreadySigned && !this.readOnlyMode
    },
    smsRequired() {
      if (this.alreadySigned) {
        return false
      }
      if (this.readOnlyMode) {
        return this.pageInfo.smsRequired !== false
      }
      return true
    },
    bankName() {
      const bankId = this.readOnlyMode || this.alreadySigned
        ? this.pageInfo.bankID
        : this.postData.bankID
      if (this.pageInfo.bankName && (this.alreadySigned || this.readOnlyMode)) {
        return this.pageInfo.bankName
      }
      const bank = this.bankIdList.find(item => item.bank_id === bankId)
      return bank ? bank.bank_name : (bankId || '请选择银行')
    },
    cardTypeText() {
      const cardType = (this.readOnlyMode || this.alreadySigned)
        ? this.pageInfo.cardType
        : this.postData.cardType
      if (cardType === 2 || cardType === '2') {
        return '信用卡'
      }
      return '借记卡'
    },
    bankDisplayName() {
      if (!this.bankName || this.bankName === '请选择银行') {
        return '请选择银行'
      }
      return `${this.bankName}${this.cardTypeText}`
    },
    filteredBankList() {
      const keyword = (this.bankKeyword || '').trim()
      if (!keyword) {
        return this.bankIdList
      }
      return this.bankIdList.filter(item => item.bank_name.indexOf(keyword) >= 0)
    },
    merchantNameText() {
      return this.pageInfo.mchName || this.pageInfo.mchShortName || ''
    },
    merchantContactTel() {
      return this.pageInfo.mchContactTel || this.pageInfo.contactTel || ''
    },
    submitBtnText() {
      if (this.submitting) {
        return this.alreadySigned ? '跳转中...' : '绑卡中...'
      }
      return this.alreadySigned ? '返回商户' : '确定'
    },
    currentBankId() {
      if (this.readOnlyMode || this.alreadySigned) {
        return this.pageInfo.bankID
      }
      return this.postData.bankID
    },
    activeProtocols() {
      return getProtocolsByBankId(this.currentBankId)
    },
    effectiveProtocols() {
      if (this.protocolFallbackActive) {
        return getDefaultProtocols()
      }
      return this.activeProtocols
    },
    currentProtocolTitle() {
      const list = this.effectiveProtocols
      if (!list.length) {
        return '快捷支付协议'
      }
      return list[this.activeProtocolIndex] ? list[this.activeProtocolIndex].title : list[0].title
    }
  },
  watch: {
    'postData.bankID'() {
      this.resetProtocolAgreement()
    },
    showProtocol(val) {
      this.syncBodyScrollLock(val || this.showBankSheet || this.customerVisible)
    },
    showBankSheet(val) {
      this.syncBodyScrollLock(val || this.showProtocol || this.customerVisible)
    },
    customerVisible(val) {
      this.syncBodyScrollLock(val || this.showProtocol || this.showBankSheet)
    },
    merchantNameText() {
      this.updatePageTitle()
    }
  },
  mounted() {
    this.defaultDocumentTitle = document.title || '收银台'
    this.updatePageTitle()
    this.setupMobilePage()
    this.syncBodyScrollLock(false)
    this.init()
    this.$nextTick(() => {
      this.syncDesktopLayout()
    })
  },
  beforeDestroy() {
    this.clearSmsTimer()
    this.clearRedirectCountdown()
    this.syncBodyScrollLock(false)
    this.restorePageTitle()
    this.teardownMobilePage()
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
  },
  methods: {
    updatePageTitle() {
      const merchantName = this.merchantNameText
      document.title = merchantName ? `${merchantName}协议绑卡` : '协议绑卡'
    },
    restorePageTitle() {
      document.title = this.defaultDocumentTitle || '收银台'
    },
    getBankShort(bankName) {
      if (!bankName) {
        return '银'
      }
      const name = String(bankName).replace(/^中国/, '')
      return name.charAt(0) || '银'
    },
    init() {
      const { ifCode, token } = this.$route.params
      this.ifCode = ifCode
      this.orderToken = token
      if (!ifCode || !token) {
        this.loading = false
        this.loadError = '页面参数无效，请通过商户提供的绑卡链接进入'
        return
      }
      loadAgreementManifestFromServer()
        .then(() => getOpenUpFastPageInfo({ token }))
        .then(info => {
          this.pageInfo = info || {}
          if (info && info.bankID != null && info.bankID !== '') {
            this.postData.bankID = String(info.bankID)
          }
          if (info && info.cardType != null) {
            this.postData.cardType = info.cardType
          }
          if (info && info.readOnlyMode) {
            if (info.bindingTxSN) {
              this.postData.token = info.bindingTxSN
            }
            if (info.smsRequired !== false) {
              this.smsSent = true
            }
          }
          this.postData.cardType = this.postData.cardType || 1
          this.resetProtocolAgreement()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.loadError = (err && err.msg) || '加载失败，请稍后重试'
        })
    },
    onSmsInput(e) {
      this.validCode = (e.target.value || '').replace(/\D/g, '').slice(0, 6)
    },
    onPanInput(e) {
      this.postData.pan = (e.target.value || '').replace(/\D/g, '')
    },
    onPhoneInput(e) {
      this.postData.phoneNo = (e.target.value || '').replace(/\D/g, '').slice(0, 11)
    },
    setupMobilePage() {
      document.documentElement.classList.add('open-fastpay-html')
      document.body.classList.add('open-fastpay-body')
      const app = document.getElementById('app')
      if (app) {
        app.classList.add('open-fastpay-app')
      }
      this.syncDesktopLayout()
      this._onViewportChange = () => {
        this.syncDesktopLayout()
      }
      window.addEventListener('resize', this._onViewportChange)
      window.addEventListener('orientationchange', this._onViewportChange)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._onViewportChange)
      }
      this.$nextTick(() => {
        this.applyRemBase()
      })
    },
    teardownMobilePage() {
      document.documentElement.classList.remove('open-fastpay-html', 'open-fastpay-desktop', 'open-fastpay-scroll-lock')
      document.body.classList.remove('open-fastpay-body', 'open-fastpay-desktop', 'open-fastpay-scroll-lock')
      const app = document.getElementById('app')
      if (app) {
        app.classList.remove('open-fastpay-app', 'open-fastpay-desktop')
      }
      if (this._onViewportChange) {
        window.removeEventListener('resize', this._onViewportChange)
        window.removeEventListener('orientationchange', this._onViewportChange)
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this._onViewportChange)
        }
        this._onViewportChange = null
      }
      document.documentElement.style.removeProperty('--open-fastpay-shell-width')
      if (document.documentElement.style.getPropertyPriority('font-size') === 'important') {
        document.documentElement.style.removeProperty('font-size')
      }
    },
    syncDesktopLayout() {
      const desktop = window.innerWidth >= DESKTOP_BREAKPOINT
      const wide = window.innerWidth >= DESKTOP_WIDE_BREAKPOINT
      const className = 'open-fastpay-desktop'
      document.documentElement.classList.toggle(className, desktop)
      document.body.classList.toggle(className, desktop)
      const app = document.getElementById('app')
      if (app) {
        app.classList.toggle(className, desktop)
      }
      if (this.$el) {
        this.$el.classList.toggle('open-fastpay-layout-wide', desktop && wide)
      }
      this.applyDesktopShellWidth(desktop, wide)
      this.applyRemBase()
      if (desktop) {
        window.requestAnimationFrame(() => {
          this.applyRemBase()
          this.applyDesktopShellWidth(desktop, wide)
        })
        window.setTimeout(() => {
          this.applyRemBase()
          this.applyDesktopShellWidth(desktop, wide)
        }, 80)
      }
    },
    applyDesktopShellWidth(desktop, wide) {
      if (!desktop) {
        document.documentElement.style.removeProperty('--open-fastpay-shell-width')
        return
      }
      const shellWidth = wide
        ? Math.min(DESKTOP_SHELL_WIDE_MAX, window.innerWidth - 72)
        : DESKTOP_SHELL_WIDTH
      document.documentElement.style.setProperty('--open-fastpay-shell-width', `${shellWidth}px`)
    },
    applyRemBase() {
      const width = document.documentElement.clientWidth || window.innerWidth
      if (width >= DESKTOP_BREAKPOINT) {
        document.documentElement.style.setProperty('font-size', `${DESKTOP_REM_BASE}px`, 'important')
        return
      }
      document.documentElement.style.setProperty('font-size', `${width / 10}px`, 'important')
    },
    onInputFocus(e) {
      const target = e && e.target
      if (!target || typeof target.scrollIntoView !== 'function') {
        return
      }
      this.$nextTick(() => {
        setTimeout(() => {
          target.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }, 280)
      })
    },
    openBankSheet() {
      if (this.alreadySigned || this.readOnlyMode) {
        return
      }
      this.showBankSheet = true
      this.bankKeyword = ''
    },
    closeBankSheet() {
      this.showBankSheet = false
    },
    selectBank(item) {
      this.postData.bankID = String(item.bank_id)
      this.closeBankSheet()
    },
    resetProtocolAgreement() {
      this.protocolAgreed = false
      this.protocolFallbackActive = false
      this.protocolReachBottom = false
      this.protocolHtml = ''
      this.protocolLoadError = ''
      this.activeProtocolIndex = 0
      this.protocolCache = {}
    },
    syncBodyScrollLock(locked) {
      const className = 'open-fastpay-scroll-lock'
      document.body.classList.toggle(className, locked)
      document.documentElement.classList.toggle(className, locked)
      if (!locked) {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.left = ''
        document.body.style.right = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        document.documentElement.style.overflow = ''
      }
    },
    isDefaultProtocolList(list) {
      return list.length === 3 && list.every(item => String(item.id).indexOf('cpcn-') === 0)
    },
    activateProtocolFallback() {
      if (this.protocolFallbackActive || this.isDefaultProtocolList(this.activeProtocols)) {
        return false
      }
      this.protocolFallbackActive = true
      this.protocolAgreed = false
      this.protocolReachBottom = false
      this.protocolHtml = ''
      this.activeProtocolIndex = 0
      this.toast('未找到该银行专用协议，已展示中金默认协议')
      return true
    },
    protocolToastName() {
      return this.effectiveProtocols.map(item => `《${item.title}》`).join('、')
    },
    validateCardForm() {
      if (!this.postData.bankID) {
        this.toast('请选择付款银行')
        return false
      }
      if ((this.postData.pan || '').length < 12) {
        this.toast('请输入正确的银行卡号')
        return false
      }
      if (!(this.postData.cardHolderName || '').trim()) {
        this.toast('请输入持卡人姓名')
        return false
      }
      if (!(this.postData.cardHolderId || '').trim()) {
        this.toast('请输入证件号')
        return false
      }
      if ((this.postData.phoneNo || '').length < 11) {
        this.toast('请输入正确的手机号')
        return false
      }
      return true
    },
    clearSmsTimer() {
      if (this.smsTimer) {
        clearTimeout(this.smsTimer)
        this.smsTimer = null
      }
    },
    startSmsCooldown() {
      this.smsBtnDisabled = true
      this.smsCooldown = SMS_COOLDOWN
      const tick = () => {
        if (this.smsCooldown <= 0) {
          this.resetSmsBtn()
          return
        }
        this.smsBtnText = `${this.smsCooldown}s后重发`
        this.smsCooldown -= 1
        this.smsTimer = setTimeout(tick, 1000)
      }
      tick()
    },
    resetSmsBtn() {
      this.clearSmsTimer()
      this.smsBtnDisabled = false
      this.smsBtnText = SMS_BTN_TEXT
      this.smsCooldown = SMS_COOLDOWN
    },
    sendBindSms() {
      if (this.smsBtnDisabled) {
        return
      }
      if (!this.protocolAgreed) {
        this.toast(`请先阅读并同意${this.protocolToastName()}`)
        return
      }
      if (!this.validateCardForm()) {
        return
      }
      this.smsBtnDisabled = true
      this.smsBtnText = '发送中...'
      openUpFastBindSendSms(this.ifCode, this.orderToken, this.postData)
        .then(res => {
          this.postData.token = res.token || res.bindingTxSN || ''
          if (res.bindStatus === 'success' && res.smsRequired === false) {
            this.pageInfo.alreadySigned = true
            this.pageInfo.bindStatus = 'success'
            this.smsSent = false
            this.resetSmsBtn()
            this.toast('该卡已签约，可直接返回商户')
          } else {
            this.smsSent = true
            this.pageInfo.bindStatus = 'sms_sent'
            this.startSmsCooldown()
            this.toast('验证码已发送，请查收短信')
          }
        })
        .catch(err => {
          this.smsSent = false
          this.resetSmsBtn()
          this.toast((err && err.msg) || '获取验证码失败')
        })
    },
    toast(text) {
      this.toastText = text
      this.toastVisible = true
      if (this.toastTimer) {
        clearTimeout(this.toastTimer)
      }
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false
      }, 1800)
    },
    openCustomerService() {
      if (!this.merchantContactTel) {
        this.toast('暂未配置商户客服电话')
        return
      }
      this.customerVisible = true
    },
    closeCustomerService() {
      this.customerVisible = false
    },
    toggleAgreeQuick() {
      if (!this.protocolAgreed) {
        this.openProtocol()
      }
    },
    openProtocol(index = 0) {
      this.activeProtocolIndex = index
      this.showProtocol = true
      this.protocolReachBottom = false
      this.loadProtocolContent()
    },
    switchProtocolTab(index) {
      if (this.activeProtocolIndex === index) {
        return
      }
      this.activeProtocolIndex = index
      this.protocolReachBottom = false
      this.loadProtocolContent()
    },
    loadProtocolContent() {
      const list = this.effectiveProtocols
      const current = list[this.activeProtocolIndex] || list[0]
      if (!current) {
        this.protocolLoadError = '未找到可用协议'
        return
      }
      if (this.protocolCache[current.id]) {
        this.protocolHtml = this.protocolCache[current.id]
        this.protocolLoading = false
        this.protocolLoadError = ''
        this.$nextTick(() => this.checkProtocolScrollState())
        return
      }
      this.protocolLoading = true
      this.protocolLoadError = ''
      this.protocolHtml = ''
      fetch(current.url, { cache: 'no-cache' })
        .then(res => {
          if (!res.ok) {
            throw new Error('协议文件不存在')
          }
          return res.arrayBuffer()
        })
        .then(buffer => {
          const html = this.decodeAgreementHtml(buffer)
          const content = this.extractProtocolBody(html)
          this.protocolCache[current.id] = content
          this.protocolHtml = content
          this.protocolLoading = false
          this.$nextTick(() => this.checkProtocolScrollState())
        })
        .catch(() => {
          if (this.activateProtocolFallback()) {
            this.loadProtocolContent()
            return
          }
          this.protocolLoading = false
          this.protocolLoadError = '协议加载失败，请联系商户或稍后重试'
        })
    },
    checkProtocolScrollState() {
      const el = this.$refs.protocolBody
      if (!el) {
        return
      }
      el.scrollTop = 0
      this.protocolReachBottom = el.scrollHeight <= el.clientHeight + 8
    },
    decodeAgreementHtml(buffer) {
      const headSample = new TextDecoder('utf-8').decode(buffer.slice(0, 4096))
      const charsetMatch = headSample.match(/charset\s*=\s*["']?\s*([\w-]+)/i)
      const charset = charsetMatch ? charsetMatch[1].toLowerCase() : 'utf-8'
      const candidates = [charset]
      if (charset === 'gb2312' || charset === 'gbk') {
        candidates.push('gbk', 'gb18030')
      }
      for (const label of candidates) {
        try {
          return new TextDecoder(label).decode(buffer)
        } catch (err) {
          // try next decoder
        }
      }
      return new TextDecoder('utf-8').decode(buffer)
    },
    extractProtocolBody(html) {
      if (!html) {
        return ''
      }
      const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
      return match ? match[1] : html
    },
    closeProtocol() {
      this.showProtocol = false
    },
    onProtocolScroll(e) {
      const el = e.target
      const reachBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
      if (reachBottom) {
        this.protocolReachBottom = true
      }
    },
    confirmProtocol() {
      if (!this.protocolReachBottom) {
        return
      }
      if (this.effectiveProtocols.length > 1 && this.activeProtocolIndex < this.effectiveProtocols.length - 1) {
        this.switchProtocolTab(this.activeProtocolIndex + 1)
        this.toast('请继续阅读下一份协议')
        return
      }
      this.protocolAgreed = true
      this.showProtocol = false
    },
    appendQuery(url, params) {
      try {
        const u = new URL(url, window.location.origin)
        Object.keys(params).forEach(key => {
          if (params[key] != null && params[key] !== '') {
            u.searchParams.set(key, params[key])
          }
        })
        return u.toString()
      } catch (e) {
        const sep = url.indexOf('?') >= 0 ? '&' : '?'
        const qs = Object.keys(params)
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
          .join('&')
        return url + sep + qs
      }
    },
    clearRedirectCountdown() {
      if (this.redirectCountdownTimer) {
        clearInterval(this.redirectCountdownTimer)
        this.redirectCountdownTimer = null
      }
    },
    maskCardNo(pan) {
      const value = (pan || '').replace(/\s+/g, '')
      if (!value) {
        return '-'
      }
      if (value.length <= 8) {
        return value
      }
      return `${value.slice(0, 4)} **** **** ${value.slice(-4)}`
    },
    buildMerchantRedirectUrl(extraParams) {
      if (!this.pageInfo.returnUrl) {
        return ''
      }
      const redirectParams = Object.assign({ payOrderId: this.pageInfo.payOrderId }, extraParams || {})
      return this.appendQuery(this.pageInfo.returnUrl, redirectParams)
    },
    showBindSuccessPage(redirectUrl) {
      this.submitting = false
      this.resultView = 'success'
      this.resultErrorMsg = ''
      this.successPanMask = this.pageInfo.panMask || this.maskCardNo(this.postData.pan)
      this.successCardHolderName = this.pageInfo.cardHolderName || this.postData.cardHolderName || ''
      this.pendingRedirectUrl = redirectUrl || ''
      this.redirectCountdown = REDIRECT_COUNTDOWN
      this.clearRedirectCountdown()
      if (this.pendingRedirectUrl) {
        this.redirectCountdownTimer = setInterval(() => {
          this.redirectCountdown -= 1
          if (this.redirectCountdown <= 0) {
            this.clearRedirectCountdown()
            this.goToMerchant()
          }
        }, 1000)
      }
    },
    showBindFailPage(message) {
      this.submitting = false
      this.resultView = 'fail'
      this.resultErrorMsg = message || '绑卡失败，请稍后重试'
      this.clearRedirectCountdown()
    },
    retryBind() {
      this.resultView = ''
      this.resultErrorMsg = ''
      this.validCode = ''
    },
    finishWithoutRedirect() {
      this.toast('绑卡已完成')
    },
    goToMerchant() {
      if (!this.pendingRedirectUrl) {
        return
      }
      this.clearRedirectCountdown()
      window.location.href = this.pendingRedirectUrl
    },
    redirectToMerchant(extraParams) {
      const redirectUrl = this.buildMerchantRedirectUrl(extraParams)
      this.showBindSuccessPage(redirectUrl)
    },
    handleSubmit() {
      if (!this.alreadySigned && !this.protocolAgreed) {
        this.toast(`请先阅读并同意${this.protocolToastName()}`)
        return
      }

      if (this.alreadySigned) {
        this.submitting = true
        this.redirectToMerchant({ bindStatus: 'success' })
        return
      }

      if (!this.readOnlyMode && (!this.smsSent || !this.postData.token)) {
        this.toast('请先获取短信验证码')
        return
      }
      const code = (this.validCode || '').trim()
      if (!code) {
        this.toast('请输入短信验证码')
        return
      }
      if (!this.readOnlyMode && !this.validateCardForm()) {
        return
      }

      this.submitting = true
      const confirmData = this.readOnlyMode
        ? { validCode: code }
        : Object.assign({}, this.postData, { validCode: code })
      openUpFastBindConfirm(this.ifCode, this.orderToken, confirmData)
        .then(res => {
          this.postData.token = (res && (res.token || res.bindingTxSN)) || this.postData.token
          this.pageInfo.bindStatus = 'success'
          this.pageInfo.alreadySigned = true
          const redirectUrl = (res && res.redirectUrl) || this.buildMerchantRedirectUrl({ bindStatus: 'success' })
          this.showBindSuccessPage(redirectUrl)
        })
        .catch(err => {
          this.showBindFailPage((err && err.msg) || '绑卡失败，请稍后重试')
        })
    }
  }
}
</script>
